import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowRight, Loader2, Mic, Search, Square } from 'lucide-react';
import { updateMyPassword } from '../../services/api';
import { aaiApi } from '../../services/aaiApi';
import { transcribeAudio } from '../../orbit/lib/intakeApi';
import { PasswordField } from '../auth/AuthShell';
import { errorText, setupApi } from '../../services/setupApi';
import type { CatalogGroup, SetupApp, SetupView } from '../../services/setupApi';
import {
  AppMark, BTN, BTN_TEXT, FIELD_LABEL, FIRST_BUTTON, FIRST_INPUT, FIRST_LABEL, FirstSessionCard, Footer, INPUT, PANEL,
  SetupFrame, StepTitle,
} from './setupKit';

export const SET_PASSWORD_FLAG = 'orbit_set_password';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const firstName = (name: string): string => (name || '').trim().split(/\s+/)[0] || '';

/* A partner-created owner's account carries the company name as the person's
   name, so that is not a name we know. */
const knowsName = (view: SetupView): boolean => {
  const name = (view.name || '').trim();
  if (!name || name.includes('@')) return false;
  return name.toLowerCase() !== (view.company_name || '').trim().toLowerCase();
};

type Props = { view: SetupView; onView: (v: SetupView) => void };

const startingJobTitle = (view: SetupView): string => {
  const known = view.setup?.about?.job_title || view.job_title;
  if (known) return known;
  if (view.role === 'OWNER') return 'Owner';
  return '';
};

// ── 00 Welcome ──────────────────────────────────────────────────────────────

export const WelcomeStep: React.FC<Props> = ({ view, onView }) => {
  const needsPassword = localStorage.getItem(SET_PASSWORD_FLAG) === '1';
  const needsName = !knowsName(view);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState(view.phone || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  // The phone is optional and asked where they joined, so it never holds this screen up on its own.
  const nothingToAsk = !needsName && !needsPassword;

  // Their name is known and no password is due: there is nothing to ask here.
  const moved = useRef(false);
  useEffect(() => {
    if (!nothingToAsk || moved.current) return;
    moved.current = true;
    setupApi.step('about').then(({ data }) => onView(data)).catch(() => { moved.current = false; });
  }, [nothingToAsk, onView]);

  let title = 'Welcome. One thing before we start.';
  let lead = 'Add what is missing and you are in.';
  if (!needsName) {
    title = `Hey ${firstName(view.name)}, one thing before we start.`;
    lead = view.role === 'OWNER'
      ? 'We have your name and email. What is the best number to reach you if we need to?'
      : 'We have your name and email from your invite. What is the best number to reach you if we need to?';
  }
  if (needsPassword) lead = 'Your partner set up the company. We have your email. Add your name, the best number to reach you, and choose a password.';

  /* Google in place of a password: the account is the one the partner made,
     so the server only accepts the Google account for that email. */
  const continueWithGoogle = () => {
    localStorage.removeItem(SET_PASSWORD_FLAG);
    window.location.href = `${API_BASE_URL}/api/auth/google?login_hint=${encodeURIComponent(view.email)}`;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (needsName && !name.trim()) { toast.error('Add your name.'); return; }
    if (needsPassword && password.length < 8) { toast.error('Choose a password of 8 characters or more.'); return; }
    if (needsPassword && password !== confirm) { toast.error('The two passwords do not match.'); return; }
    setBusy(true);
    try {
      if (needsPassword) {
        await updateMyPassword({ new_password: password, confirm_password: confirm });
        localStorage.removeItem(SET_PASSWORD_FLAG);
      }
      const { data } = await setupApi.welcome({ name: needsName ? name.trim() : undefined, phone });
      if (needsName && name.trim()) localStorage.setItem('user_name', name.trim());
      onView(data);
    } catch (err) {
      toast.error(errorText(err, 'Could not save that. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (nothingToAsk) {
    return <div className="flex min-h-screen items-center justify-center bg-[#f6f6f4]"><Loader2 size={22} className="animate-spin text-[#92949d]" /></div>;
  }

  return (
    <FirstSessionCard title={title} lead={lead}>
      <form onSubmit={submit} className="space-y-5">
        {needsName && (
          <div>
            <label htmlFor="setup-name" className={FIRST_LABEL}>Name</label>
            <input id="setup-name" className={FIRST_INPUT} value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" autoComplete="name" autoFocus />
          </div>
        )}
        <div>
          <label htmlFor="setup-phone" className={FIRST_LABEL}>Phone number <span className="font-normal text-[#92949d]">(optional)</span></label>
          <input id="setup-phone" type="tel" className={FIRST_INPUT} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 0100" autoComplete="tel" autoFocus={!needsName} />
        </div>
        {needsPassword && (
          <>
            <PasswordField id="setup-password" label="Choose a password" value={password} onChange={setPassword} placeholder="8 characters or more" autoComplete="new-password" labelClassName={FIRST_LABEL} inputClassName={FIRST_INPUT} />
            <PasswordField id="setup-password-confirm" label="Type it again" value={confirm} onChange={setConfirm} placeholder="Same password" autoComplete="new-password" labelClassName={FIRST_LABEL} inputClassName={FIRST_INPUT} />
          </>
        )}
        <button type="submit" className={FIRST_BUTTON} disabled={busy}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : null}Continue <ArrowRight size={18} />
        </button>
        {needsPassword && (
          <>
            <div className="flex items-center gap-3 text-[12px] text-[#92949d]"><span className="h-px flex-1 bg-[#e7e7e4]" />or skip the password<span className="h-px flex-1 bg-[#e7e7e4]" /></div>
            <button type="button" onClick={continueWithGoogle} className="flex w-full items-center justify-center gap-2 rounded-[10px] border border-[#dedede] bg-white px-4 py-3 text-[14px] font-semibold text-[#18191d] hover:border-[#18191d]">
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>
            <p className="text-center text-[12px] text-[#92949d]">Use the Google account for {view.email}. You sign in with Google from then on.</p>
          </>
        )}
      </form>
    </FirstSessionCard>
  );
};

// ── 01 About you ────────────────────────────────────────────────────────────

export const AboutStep: React.FC<Props> = ({ view, onView }) => {
  const saved = view.setup?.about || {};
  const [jobTitle, setJobTitle] = useState(startingJobTitle(view));
  const [department, setDepartment] = useState(saved.department || view.department || '');
  const [work, setWork] = useState(saved.work || '');
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const startVoice = async () => {
    if (!navigator.mediaDevices) { toast.error('This browser cannot record audio.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        try {
          const text = await transcribeAudio(new Blob(chunks.current, { type: rec.mimeType || 'audio/webm' }), localStorage.getItem('client_id'));
          setWork((w) => [w.trim(), text.trim()].filter(Boolean).join(' '));
        } catch {
          toast.error('Could not hear that. Try again or type it.');
        } finally {
          setTranscribing(false);
        }
      };
      recorder.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error('The microphone is not available.');
    }
  };
  const stopVoice = () => {
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
    setRecording(false);
  };

  const addDocument = async (file: File) => {
    if (/\.(txt|md|csv)$/i.test(file.name) || file.type.startsWith('text/')) {
      const text = await file.text();
      setWork((w) => [w.trim(), text.trim().slice(0, 3000)].filter(Boolean).join('\n\n'));
      return;
    }
    setWork((w) => [w.trim(), `(See my document: ${file.name})`].filter(Boolean).join('\n'));
    toast.info(`${file.name} added. Plain text files are read straight into your answer.`);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await setupApi.about({ job_title: jobTitle, department, work });
      onView(data);
    } catch (err) {
      toast.error(errorText(err, 'Could not save that. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    setBusy(true);
    try {
      const { data } = await setupApi.step('apps', 'about');
      onView(data);
    } catch (err) {
      toast.error(errorText(err, 'Could not move on. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  // The Owner runs the company, so they are not asked for a department.
  const asksDepartment = view.role !== 'OWNER' && view.role !== 'EXECUTIVE';
  let departmentField: React.ReactNode = null;
  if (view.department_locked) {
    departmentField = (
      <div className={`${INPUT} flex items-center justify-between bg-[#eef3ff]`}>
        <span>{view.department || department}</span>
        <span className="font-mono text-[10.5px] text-[#666971]">Set by your Owner</span>
      </div>
    );
  }
  if (!view.department_locked && view.may_create_department) {
    departmentField = (
      <>
        <input id="setup-department" list="setup-departments" className={INPUT} value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="Leadership" />
        <datalist id="setup-departments">{view.departments.map((d) => <option key={d} value={d} />)}</datalist>
      </>
    );
  }
  if (!view.department_locked && !view.may_create_department && view.departments.length > 0) {
    departmentField = (
      <select id="setup-department" className={INPUT} value={department} onChange={(e) => setDepartment(e.target.value)}>
        <option value="">Choose your department</option>
        {view.departments.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
    );
  }
  if (!view.department_locked && !view.may_create_department && view.departments.length === 0) {
    departmentField = <div className={`${INPUT} text-[#777982]`}>Your Owner has not added departments yet</div>;
  }

  return (
    <SetupFrame step="about">
      <StepTitle
        eyebrow="About you"
        title="Help your workspace understand your role."
        lead={asksDepartment
          ? 'Role and department are enough to start. One natural answer, by voice, text or document, tells Orbit what people rely on you for.'
          : 'Your job title is enough to start. One natural answer, by voice, text or document, tells Orbit what people rely on you for.'}
      />
      <form onSubmit={submit} className={`${PANEL} space-y-4`}>
        <div className={`grid gap-4 ${asksDepartment ? 'md:grid-cols-2' : ''}`}>
          <div>
            <label htmlFor="setup-title" className={FIELD_LABEL}>Job title</label>
            <input id="setup-title" className={INPUT} value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder={asksDepartment ? 'Operations lead' : 'Founder and CEO'} />
          </div>
          {asksDepartment && (
            <div>
              <label htmlFor="setup-department" className={FIELD_LABEL}>Department</label>
              {departmentField}
            </div>
          )}
        </div>
        <div className="grid gap-4 rounded-[10px] bg-[#eef3ff] p-4 md:grid-cols-[200px_1fr]">
          <div className="space-y-2">
            <span className="flex h-9 w-9 items-center justify-center gap-[5px] rounded-[9px] bg-[#ffea00]"><i className="block h-[7px] w-[4px] rounded-full bg-[#18191d]" /><i className="block h-[7px] w-[4px] rounded-full bg-[#18191d]" /></span>
            <b className="block text-[14px] text-[#18191d]">Tell us about your work</b>
            <p className="text-[12.5px] leading-[1.5] text-[#666971]">What are you responsible for, and what is one part you want to improve?</p>
            {recording
              ? <button type="button" className={BTN} onClick={stopVoice}><Square size={13} />Stop</button>
              : <button type="button" className={BTN} onClick={startVoice} disabled={transcribing}>{transcribing ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />}{transcribing ? 'Listening back' : 'Use voice'}</button>}
          </div>
          <div className="space-y-2">
            <textarea
              id="setup-work"
              className="min-h-[120px] w-full rounded-[8px] border border-[#dedede] bg-white p-3 text-[14px] text-[#18191d] outline-none placeholder:text-[#92949d] focus:border-[#18191d]"
              value={work}
              onChange={(e) => setWork(e.target.value)}
              placeholder="Or type naturally here..."
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button type="button" className={BTN_TEXT} onClick={() => fileRef.current?.click()}>Add document</button>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) addDocument(f); e.target.value = ''; }} />
              <span className="text-[12px] text-[#777982]">One answer is enough.</span>
            </div>
          </div>
        </div>
        <Footer
          left={<button type="button" className={BTN_TEXT} onClick={skip} disabled={busy}>Skip for now</button>}
          right={<button type="submit" className={BTN} disabled={busy || recording}>{busy && <Loader2 size={15} className="animate-spin" />}Continue to apps</button>}
        />
      </form>
    </SetupFrame>
  );
};

// ── 02 Your apps ────────────────────────────────────────────────────────────

export const AppsStep: React.FC<Props> = ({ view, onView }) => {
  const [groups, setGroups] = useState<CatalogGroup[]>([]);
  const [picked, setPicked] = useState<SetupApp[]>(view.setup?.apps || []);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<SetupApp[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setupApi.catalog().then(({ data }) => setGroups(data.groups)).catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setFound([]); return; }
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await aaiApi.searchTools(q);
        setFound(data.tools.map((t) => ({ slug: t.slug, label: t.label, logo: t.logo })));
      } catch {
        setFound([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const chosen = useMemo(() => new Set(picked.map((a) => a.slug)), [picked]);
  const toggle = (app: SetupApp) => {
    if (chosen.has(app.slug)) { setPicked((p) => p.filter((a) => a.slug !== app.slug)); return; }
    setPicked((p) => [...p, app]);
  };

  const q = query.trim().toLowerCase();
  const shown = groups
    .map((g) => ({ ...g, apps: g.apps.filter((a) => !q || a.label.toLowerCase().includes(q)) }))
    .filter((g) => g.apps.length > 0);
  const inCatalog = new Set(groups.flatMap((g) => g.apps.map((a) => a.slug)));
  const extra = found.filter((a) => !inCatalog.has(a.slug));
  const pickedOutside = picked.filter((a) => !inCatalog.has(a.slug) && !extra.some((e) => e.slug === a.slug));

  const save = async () => {
    setBusy(true);
    try {
      if (picked.length === 0) {
        const { data } = await setupApi.step('ideas', 'apps');
        onView(data);
        return;
      }
      const { data } = await setupApi.apps(picked);
      onView(data);
    } catch (err) {
      toast.error(errorText(err, 'Could not save your apps. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    setBusy(true);
    try {
      const { data } = await setupApi.step('ideas', 'apps');
      onView(data);
    } catch (err) {
      toast.error(errorText(err, 'Could not move on. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const tile = (app: SetupApp) => {
    const on = chosen.has(app.slug);
    return (
      <button
        key={app.slug}
        type="button"
        onClick={() => toggle(app)}
        aria-pressed={on}
        className={`flex min-w-0 items-center gap-2.5 rounded-[8px] border px-2.5 py-2 text-left text-[13px] font-medium text-[#18191d] transition-colors ${on ? 'border-[#18191d] bg-[#eef3ff]' : 'border-[#e6e6e3] bg-white hover:border-[#b9b9b6]'}`}
      >
        <AppMark app={app} on={on} />
        <span className="truncate">{app.label}</span>
      </button>
    );
  };

  const section = (key: string, label: string, apps: SetupApp[], note?: string) => (
    <section key={key} className="border-t border-[#ececea] px-5 py-4 first:border-t-0">
      <header className="mb-3 flex items-baseline justify-between">
        <b className="text-[13.5px] text-[#18191d]">{label}</b>
        <small className="font-mono text-[11px] text-[#777982]">{note || `${apps.length} apps`}</small>
      </header>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">{apps.map(tile)}</div>
    </section>
  );

  return (
    <SetupFrame step="apps" wide>
      <StepTitle
        eyebrow="Your apps"
        title="Which apps do you use, or want to use?"
        lead="Nothing connects here and no permission is asked. Pick the ones you recognize."
        aside={<div className="max-w-[300px] rounded-[10px] bg-[#eef3ff] p-4 text-[13px] leading-[1.5]"><b className="block text-[#18191d]">Nothing connects during setup.</b><span className="text-[#666971]">You can skip this and add apps later.</span></div>}
      />
      <div className="mb-4 flex items-center gap-3 rounded-[10px] border border-[#e6e6e3] bg-white px-4">
        <Search size={15} className="text-[#777982]" />
        <input
          id="setup-app-search"
          className="h-[44px] flex-1 bg-transparent text-[14px] text-[#18191d] outline-none placeholder:text-[#92949d]"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search over 1,600 apps"
        />
        {searching && <Loader2 size={14} className="animate-spin text-[#777982]" />}
        <small className="font-mono text-[11.5px] text-[#777982]">{picked.length} selected</small>
      </div>
      <div className="overflow-hidden rounded-[12px] border border-[#e6e6e3] bg-white">
        {pickedOutside.length > 0 && section('picked', 'Your other picks', pickedOutside)}
        {extra.length > 0 && section('search', 'More apps', extra, 'from the full list')}
        {shown.map((g) => section(g.id, g.label, g.apps))}
        {groups.length === 0 && <div className="flex justify-center p-10"><Loader2 size={18} className="animate-spin text-[#777982]" /></div>}
        {groups.length > 0 && shown.length === 0 && extra.length === 0 && !searching && (
          <p className="p-6 text-[13.5px] text-[#666971]">No app matches "{query}".</p>
        )}
      </div>
      <Footer
        left={<button type="button" className={BTN_TEXT} onClick={skip} disabled={busy}>Skip for now</button>}
        right={<button type="button" className={BTN} onClick={save} disabled={busy}>{busy && <Loader2 size={15} className="animate-spin" />}Continue</button>}
      />
    </SetupFrame>
  );
};
